import React, { useEffect, useMemo, useRef, useState } from 'react';
import { notificationService } from '../services/api';
import './NotificationBell.css';

const DISMISSED_LIVE_NOTIFICATIONS_KEY = 'constructap-dismissed-live-notifications';

const isStoredUnreadNotification = (notification) => !notification.live && !notification.read;
const isLiveNotification = (notification) => !!notification.live;
const isHighlightedNotification = (notification) => isLiveNotification(notification) || isStoredUnreadNotification(notification);
const getLiveNotificationKey = (notification) => `${notification.id}:${notification.message || ''}`;

const readDismissedLiveKeys = () => {
  try {
    const raw = localStorage.getItem(DISMISSED_LIVE_NOTIFICATIONS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch (error) {
    return new Set();
  }
};

const saveDismissedLiveKeys = (keys) => {
  localStorage.setItem(DISMISSED_LIVE_NOTIFICATIONS_KEY, JSON.stringify([...keys]));
};

const filterDismissedLiveNotifications = (items, dismissedKeys = readDismissedLiveKeys()) => (
  items.filter((notification) => !notification.live || !dismissedKeys.has(getLiveNotificationKey(notification)))
);

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const data = await notificationService.getAll();
      setNotifications(filterDismissedLiveNotifications(Array.isArray(data) ? data : []));
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    const closeOnOutsideClick = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('touchstart', closeOnOutsideClick);

    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('touchstart', closeOnOutsideClick);
    };
  }, [open]);

  const storedUnreadCount = useMemo(
    () => notifications.filter(isStoredUnreadNotification).length,
    [notifications]
  );

  const liveCount = useMemo(
    () => notifications.filter(isLiveNotification).length,
    [notifications]
  );

  const badgeCount = storedUnreadCount + liveCount;

  const dismissLiveNotifications = (items) => {
    const liveItems = items.filter(isLiveNotification);
    if (liveItems.length === 0) return;

    const dismissedKeys = readDismissedLiveKeys();
    liveItems.forEach((item) => dismissedKeys.add(getLiveNotificationKey(item)));
    saveDismissedLiveKeys(dismissedKeys);
    setNotifications((prev) => filterDismissedLiveNotifications(prev, dismissedKeys));
  };

  const handleMarkAsRead = async (notification) => {
    if (notification.live) {
      dismissLiveNotifications([notification]);
      return;
    }

    if (notification.read) return;

    try {
      const updated = await notificationService.markAsRead(notification.id);
      setNotifications((prev) => prev.map((item) => (
        item.id === notification.id ? { ...item, ...updated, read: 1 } : item
      )));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      dismissLiveNotifications(notifications);
      setNotifications((prev) => prev.map((item) => (
        item.live ? item : { ...item, read: 1 }
      )));
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const handleDeleteAll = async () => {
    if (notifications.length === 0) return;
    const confirmed = window.confirm('Naozaj chceš odstrániť všetky notifikácie?');
    if (!confirmed) return;

    try {
      await notificationService.deleteAll();
      dismissLiveNotifications(notifications);
      setNotifications([]);
    } catch (error) {
      console.error('Error deleting notifications:', error);
    }
  };

  const formatDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString('sk-SK', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="notification-bell" ref={panelRef} onMouseLeave={() => setOpen(false)}>
      <button
        type="button"
        className="notification-bell-button"
        onClick={() => setOpen((prev) => !prev)}
        aria-label="Notifikácie"
      >
        🔔
        {badgeCount > 0 && <span className="notification-count">{badgeCount > 99 ? '99+' : badgeCount}</span>}
      </button>

      {open && (
        <div className="notification-panel">
          <div className="notification-panel-header">
            <h3>Notifikácie</h3>
            <div className="notification-panel-actions">
              {notifications.length > 0 && (
                <>
                  <button
                    type="button"
                    className="notification-mark-all"
                    onClick={handleMarkAllAsRead}
                    disabled={badgeCount === 0}
                  >
                    Označiť všetko ako prečítané
                  </button>
                  <button
                    type="button"
                    className="notification-delete-all"
                    onClick={handleDeleteAll}
                  >
                    Vymazať všetky
                  </button>
                </>
              )}
              <button type="button" className="notification-refresh" onClick={loadNotifications} disabled={loading} aria-label="Obnoviť notifikácie">
                {loading ? '...' : '↻'}
              </button>
            </div>
          </div>

          {notifications.length === 0 ? (
            <div className="notification-empty">Žiadne notifikácie.</div>
          ) : (
            <div className="notification-list">
              {notifications.slice(0, 20).map((notification) => (
                <div
                  key={notification.id}
                  className={`notification-item ${isHighlightedNotification(notification) ? 'unread' : 'read'} ${notification.live ? 'live' : ''}`}
                >
                  <div className="notification-item-header">
                    <strong>{notification.title}</strong>
                    <span>{notification.live ? 'aktuálne' : formatDate(notification.created_at)}</span>
                  </div>
                  <p>{notification.message}</p>
                  <div className="notification-item-actions">
                    <span className={`notification-type ${notification.type || 'info'}`}>{notification.type || 'info'}</span>
                    {(notification.live || !notification.read) && (
                      <button type="button" onClick={() => handleMarkAsRead(notification)}>
                        Označiť ako prečítané
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
